import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  getAllPreferenceTypes,
  PreferenceItem,
  savePreference,
  getPreferencesByLeadId,
  FetchedPreferenceItem,
} from "../Services/APIService";
import {
  fetchLocationData,
  fetchProjectData,
  GetPropertyTypes,
} from "../Services/APIService";
import toast from "react-hot-toast";

interface PreferencePayload {
  tenantId: number;
  leadID: number;
  createdBy: number;
  createdOn: string;
  preferences: PreferenceItemPayload[];
}

interface PreferenceItemPayload {
  preferenceTypeID: number;
  preferenceValue: string;
  specialNotes: string;
}

interface LeadDetailsViewProps {
  leadId: number;
  onPreferenceSaved?: () => void;
}

interface PreferenceRow {
  id: number;
  typeId: number;
  typeLabel: string;
  valueId: number;
  valueLabel: string;
  specialNote: string;
}

const CreateLeadPreference: React.FC<LeadDetailsViewProps> = ({
  leadId,
  onPreferenceSaved,
}) => {
  const [preferences, setPreferences] = useState<PreferenceItem[]>([]);
  const [selectedPreferenceId, setSelectedPreferenceId] = useState<
    number | null
  >(null);
  const [secondaryOptions, setSecondaryOptions] = useState<
    { id: number; label: string }[]
  >([]);
  const [selectedSecondary, setSelectedSecondary] = useState<
    { id: number; label: string }[]
  >([]);
  const [addedPreferences, setAddedPreferences] = useState<PreferenceRow[]>([]);
  const [idCounter, setIdCounter] = useState(1);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    type?: string;
    value?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  // NEW: State for Area Preference text input
  const [areaPreferenceText, setAreaPreferenceText] = useState<string>("");

  const requiredField = <span className="text-danger ms-1">*</span>;

  const leadID = leadId;
  const tenantId = localStorage.getItem("tenantId");
  const createdBy = parseInt(localStorage.getItem("UserId") || "0", 10);
  const [savedPreferences, setSavedPreferences] = useState<
    FetchedPreferenceItem[]
  >([]);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const [prefList, savedList] = await Promise.all([
          getAllPreferenceTypes(),
          getPreferencesByLeadId(leadId),
        ]);
        setPreferences(prefList);
        setSavedPreferences(savedList);
      } catch (error) {
        console.error("Failed to fetch preferences:", error);
        toast.error("Failed to load preference types or saved preferences");
      }
    };
    fetchPreferences();
  }, [leadId]);

  const normalizeOptions = (data: any[], idKey: string, nameKey: string) =>
    data.map((item) => ({
      id: item[idKey],
      label: item[nameKey],
    }));

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedPreferenceId) return;

      // NEW: Handle Area Preference (ID 8) separately
      const selectedPref = preferences.find(
        (p) => p.id === selectedPreferenceId
      );
      const isAreaPreference =
        selectedPreferenceId === 8 ||
        selectedPref?.preferenceName === "Area Preference";

      if (isAreaPreference) {
        // Load saved Area Preference if exists
        const existingAreaPref = savedPreferences.find(
          (p) => p.preferenceTypeID === selectedPreferenceId
        );
        if (existingAreaPref) {
          setAreaPreferenceText(existingAreaPref.preferenceValue || "");
        } else {
          setAreaPreferenceText("");
        }
        setSecondaryOptions([]);
        return;
      }

      try {
        let options: { id: number; label: string }[] = [];

        if (selectedPreferenceId === 1) {
          const locations = await fetchLocationData();
          options = normalizeOptions(locations, "locationID", "locationName");
        } else if (selectedPreferenceId === 2) {
          const projects = await fetchProjectData();
          options = normalizeOptions(projects, "projectID", "projectName");
        } else if (selectedPreferenceId === 4) {
          const payload = { tenantId: parseInt(tenantId || "0") };
          const propertyTypes = await GetPropertyTypes(payload);
          options = normalizeOptions(
            propertyTypes.data.propertyTypes,
            "typeId",
            "typeName"
          );
        } else {
          if (selectedPref?.preferenceDataJson) {
            const parsed = JSON.parse(selectedPref.preferenceDataJson);
            options = normalizeOptions(parsed, "name", "name");
          }
        }

        setSecondaryOptions(options);

        const existingPrefs = savedPreferences.filter(
          (p) => p.preferenceTypeID === selectedPreferenceId
        );

        const matchedDropdownOptions: { id: number; label: string }[] = [];
        const matchedAddedPreferences: PreferenceRow[] = [];

        options.forEach((option) => {
          const matched = existingPrefs.find(
            (saved) =>
              saved.preferenceValue === option.id.toString() ||
              saved.preferenceValue === option.label
          );

          if (matched) {
            matchedDropdownOptions.push(option);

            matchedAddedPreferences.push({
              id: idCounter + Math.random(),
              typeId: selectedPreferenceId,
              typeLabel:
                preferences.find((p) => p.id === selectedPreferenceId)
                  ?.preferenceName || "",
              valueId: option.id,
              valueLabel: option.label,
              specialNote: matched.specialNotes || "",
            });
          }
        });

        setSelectedSecondary(matchedDropdownOptions);
        setAddedPreferences((prev) => [...prev, ...matchedAddedPreferences]);
        setIdCounter((prev) => prev + matchedAddedPreferences.length);
      } catch (error) {
        console.error("Failed to fetch secondary options:", error);
        toast.error("Failed to load preference values");
        setSecondaryOptions([]);
      }
    };

    fetchData();
  }, [selectedPreferenceId, preferences, savedPreferences, tenantId]);

  const validateForm = () => {
    const errors: { type?: string; value?: string } = {};
    if (!selectedPreferenceId) errors.type = "Please select a preference type.";

    if (selectedPreferenceId) {
      const selectedPref = preferences.find(
        (p) => p.id === selectedPreferenceId
      );
      const isAreaPreference =
        selectedPreferenceId === 8 ||
        selectedPref?.preferenceName === "Area Preference";

      if (isAreaPreference) {
        if (!areaPreferenceText.trim()) {
          errors.value = "Please enter area preference details.";
        }
      } else {
        if (selectedSecondary.length === 0) {
          errors.value = "Please select at least one preference value.";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) return;

    const selectedType = preferences.find((p) => p.id === selectedPreferenceId);
    if (!selectedType) return;

    const isAreaPreference =
      selectedPreferenceId === 8 ||
      selectedType.preferenceName === "Area Preference";

    // NEW: Handle Area Preference
    if (isAreaPreference) {
      // Check if this area preference is already added
      const alreadyExists = addedPreferences.some(
        (p) => p.typeId === selectedType.id
      );

      if (alreadyExists) {
        toast.error(
          "Area Preference is already added. Please edit or delete the existing one."
        );
        return;
      }

      const newItem: PreferenceRow = {
        id: idCounter + Math.random(),
        typeId: selectedType.id,
        typeLabel: selectedType.preferenceName,
        valueId: 0, // Use 0 for text-based preferences
        valueLabel: areaPreferenceText.trim(),
        specialNote: "",
      };

      setAddedPreferences((prev) => [...prev, newItem]);
      setIdCounter((prev) => prev + 1);
      setAreaPreferenceText("");
      setShowSaveButton(true);
      setFormErrors({});
      return;
    }

    // Existing logic for dropdown-based preferences
    const newItems: PreferenceRow[] = selectedSecondary
      .filter((val) => {
        return !addedPreferences.some(
          (p) => p.typeId === selectedType.id && p.valueId === val.id
        );
      })
      .map((val) => {
        const existing = savedPreferences.find(
          (s) =>
            s.preferenceTypeID === selectedType.id &&
            (s.preferenceValue === val.id.toString() ||
              s.preferenceValue === val.label)
        );

        return {
          id: idCounter + Math.random(),
          typeId: selectedType.id,
          typeLabel: selectedType.preferenceName,
          valueId: val.id,
          valueLabel: val.label,
          specialNote: existing?.specialNotes || "",
        };
      });

    if (newItems.length === 0) {
      toast.error("These preferences are already added.");
      return;
    }

    setAddedPreferences((prev) => [...prev, ...newItems]);
    setIdCounter((prev) => prev + newItems.length);
    setSelectedSecondary([]);
    setShowSaveButton(true);
    setFormErrors({});
  };

  const handleNoteChange = (id: number, note: string) => {
    setAddedPreferences((prev) =>
      prev.map((p) => (p.id === id ? { ...p, specialNote: note } : p))
    );
  };

  const handleDelete = (id: number) => {
    const updated = addedPreferences.filter((p) => p.id !== id);
    setAddedPreferences(updated);
    if (updated.length === 0) setShowSaveButton(false);
  };

  const handleSavePreference = async () => {
    if (addedPreferences.length === 0) {
      toast.error("No preferences to save.");
      return;
    }

    setIsLoading(true);
    const payload: PreferencePayload = {
      tenantId: parseInt(tenantId || "0", 10),
      leadID,
      createdBy,
      createdOn: new Date().toLocaleString("sv-SE", { timeZone: "Asia/Kolkata" }).replace(" ", "T"),
      preferences: addedPreferences.map((item) => ({
        preferenceTypeID: item.typeId,
        // For Area Preference (valueId = 0), use valueLabel directly
        preferenceValue:
          item.valueId === 0 ? item.valueLabel : item.valueId.toString(),
        specialNotes: item.specialNote,
      })),
    };

    try {
      const response = await savePreference(payload);
      console.log("API Response:", response);
      if (response.isCreated) {
        toast.success("Preferences saved successfully!");
        localStorage.setItem("preferencesSaved", "true");

        console.log(localStorage.getItem("preferencesSaved"));
        // Reset form completely
        setAddedPreferences([]);
        setShowSaveButton(false);
        setSelectedPreferenceId(null);
        setSelectedSecondary([]);
        setSecondaryOptions([]);
        setAreaPreferenceText(""); // NEW: Reset area preference text
        setFormErrors({});

        setTimeout(() => {
          if (onPreferenceSaved) {
            console.log("Calling onPreferenceSaved callback");
            onPreferenceSaved();
          }
        }, 500);
      } else {
        toast.error(response.message || "Failed to save preferences.");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("An error occurred while saving preferences.");
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Check if selected preference is Area Preference
  const selectedPref = preferences.find((p) => p.id === selectedPreferenceId);
  const isAreaPreference =
    selectedPreferenceId === 8 ||
    selectedPref?.preferenceName === "Area Preference";

  return (
    <>
      <div className="col-12 mb-3">
        <label className="form-label">Preference Type{requiredField}</label>
        <select
          className="form-select"
          value={selectedPreferenceId ?? ""}
          onChange={(e) => {
            const value = e.target.value ? parseInt(e.target.value, 10) : null;
            setSelectedPreferenceId(value);
            // Reset fields when changing preference type
            setSelectedSecondary([]);
            setAreaPreferenceText("");
            setFormErrors({});
          }}
          disabled={isLoading}
        >
          <option value="">Select Preference Type</option>
          {preferences.map((pref) => (
            <option key={pref.id} value={pref.id}>
              {pref.preferenceName}
            </option>
          ))}
        </select>
        {formErrors.type && (
          <div className="text-danger mt-1 small">{formErrors.type}</div>
        )}
      </div>

      {/* NEW: Conditional rendering - Text input for Area Preference */}
      {selectedPreferenceId && isAreaPreference && (
        <div className="col-12 mb-3">
          <label className="form-label">
            Area Preference Details{requiredField}
          </label>
          <input
            type="text"
            className="form-control"
            value={areaPreferenceText}
            onChange={(e) => setAreaPreferenceText(e.target.value)}
            placeholder="Enter area preference (e.g., Near school, quiet neighborhood)"
            disabled={isLoading}
          />
          <small className="text-muted">
            Describe the preferred area characteristics
          </small>
          {formErrors.value && (
            <div className="text-danger mt-1 small">{formErrors.value}</div>
          )}
        </div>
      )}

      {/* Existing: Dropdown for other preferences */}
      {selectedPreferenceId &&
        !isAreaPreference &&
        secondaryOptions.length > 0 && (
          <div className="col-12 mb-3">
            <label className="form-label">
              Preference Value{requiredField}
            </label>
            <Select
              isMulti
              options={secondaryOptions}
              value={selectedSecondary}
              onChange={(selected) => {
                setSelectedSecondary(
                  selected as { id: number; label: string }[]
                );
              }}
              getOptionValue={(option) => option.id.toString()}
              getOptionLabel={(option) => option.label}
              classNamePrefix="select"
              isDisabled={isLoading}
            />
            {formErrors.value && (
              <div className="text-danger mt-1 small">{formErrors.value}</div>
            )}
          </div>
        )}

      <button
        type="button"
        className="btn btn-success mb-3"
        onClick={handleAdd}
        disabled={isLoading || !selectedPreferenceId}
      >
        Add Preference
      </button>

      {addedPreferences.map((pref, idx) => (
        <div
          key={pref.id}
          className="row d-flex align-items-center mb-2 border rounded p-2"
        >
          <div className="col-md-11 form-label">
            <span className="px-2">{idx + 1}.</span> {pref.typeLabel}:{" "}
            {pref.valueLabel}
          </div>
          <div className="row">
            <div className="col-md-11 mt-3">
              <input
                type="text"
                className="form-control me-3"
                value={pref.specialNote}
                onChange={(e) => handleNoteChange(pref.id, e.target.value)}
                placeholder="Enter special note"
                disabled={isLoading}
              />
            </div>
            <div className="col-md-1 mt-3">
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(pref.id)}
                disabled={isLoading}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      ))}

      {showSaveButton && (
        <button
          type="button"
          className="btn btn-primary mt-3"
          onClick={handleSavePreference}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            "Save Preference"
          )}
        </button>
      )}
    </>
  );
};

export default CreateLeadPreference;
